'use client';

import clsx from 'clsx';
import { Modal } from '@/components/widgets/modal';
import { newbieModalStyle } from './newbie-modal.style';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { modalService } from '@/services/modal.service';
import { siteService } from '@/services/site.service';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useTheme } from 'next-themes';
import { convertApiLocale } from '@nf/utils-common/site';
import { useStaticAssetDomain } from '@/domains/config/hooks/use-static-asset-domain';

const HELPER_RESOURCE_LOCALE_LIST = [
	'ar-EG', 'bn-BD', 'de-DE', 'es-ES', 'fr-FR', 'hi-IN', 'id-ID', 'it-IT',
	'ja-JP', 'km-KH', 'ko-KR', 'lo-LA', 'ms-MY', 'my-MM', 'pt-BR', 'pt-PT',
	'ru-RU', 'ta-IN', 'th-TH', 'tl-PH', 'vi-VN', 'zh-CHS', 'zh-CN', 'zh-TW',
];

const getHelperResourceUrl = (imageServerUrl: string, locale: string) => {
	const apiLocale = convertApiLocale(locale);
	const suffix = HELPER_RESOURCE_LOCALE_LIST.includes(apiLocale) ? `.${apiLocale}` : '';
	return `${imageServerUrl}/Scripts/resource/Resource_Helper${suffix}.json?${siteService.getVersionId()}`;
};

/** [Mars] Newbie (新手版) Odds Page > Betting Tutorial Modal */
export const NewbieModal = () => {
	const t = useTranslations('General');
	const [isOpen, setIsOpen] = useState(false);
	const { SiteConfigsData: siteConfigsData } = useGetSiteConfigs();
	const locale = useLocale();
	const imageServerUrl = siteConfigsData?.ImageServerUrl;
	const sharedImgDomain = useStaticAssetDomain('SharedImg');
	const { resolvedTheme } = useTheme();
	const refIframe = useRef<HTMLIFrameElement>(null);
	const iframeReadyRef = useRef(false);
	const postedRef = useRef(false);
	const i18nConfirmedRef = useRef(false);

	const { data: helperResourceData } = useQuery({
		queryKey: ['resourceHelper', imageServerUrl, locale],
		enabled: Boolean(imageServerUrl),
		staleTime: Infinity,
		cacheTime: Infinity,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		queryFn: async () => {
			const response = await fetch(getHelperResourceUrl(imageServerUrl!, locale));
			if (!response.ok) {
				throw new Error(`${response.status}: ${response.statusText}`);
			}
			return response.json() as Promise<Record<string, string>>;
		},
	});

	useEffect(() => {
		const subscription = modalService.onOpenNewbieTutorialModal.subscribe(setIsOpen);
		return () => subscription.unsubscribe();
	}, []);

	const iframeUrl = useMemo(() => {
		if (!sharedImgDomain) return '';

		const params = new URLSearchParams({
			layout: 'mobile',
			lang: locale,
			darkmode: String(resolvedTheme === 'dark'),
			theme: 'default',
		});
		return `${sharedImgDomain}/help/Bettype_Introduce.html?${params.toString()}&${siteService.getVersionId()}`;
	}, [sharedImgDomain, locale, resolvedTheme]);

	const resetI18nState = () => {
		iframeReadyRef.current = false;
		postedRef.current = false;
		i18nConfirmedRef.current = false;
	};

	const tryPostI18nLang = useCallback(() => {
		if (postedRef.current || !iframeReadyRef.current || !helperResourceData) return;
		if (!refIframe.current?.contentWindow) return;

		refIframe.current.contentWindow.postMessage({
			message: 'get_i18n_lang',
			value: helperResourceData,
		}, '*');
		postedRef.current = true;
	}, [helperResourceData]);

	useEffect(() => {
		resetI18nState();
	}, [iframeUrl]);

	useEffect(() => {
		if (!isOpen) {
			resetI18nState();
			return;
		}

		const onMessage = (event: MessageEvent) => {
			if (event.data === 'iframe_load_complete') {
				i18nConfirmedRef.current = true;
			}
		};

		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		tryPostI18nLang();
	}, [isOpen, tryPostI18nLang]);

	const handleIframeLoad = () => {
		iframeReadyRef.current = true;
		tryPostI18nLang();
	};

	return (
		<Modal
			isOpen={isOpen}
			closeModal={() => setIsOpen(false)}
			showHeader={true}
			title={t('lbl_NBetGuide')}
		>
			<div className={clsx('scrollbar', newbieModalStyle.content)}>
				{iframeUrl ? (
					<iframe
						className={newbieModalStyle.iframe}
						ref={refIframe}
						src={iframeUrl}
						onLoad={handleIframeLoad}
					/>
				) : null}
			</div>
		</Modal>
	);
};
